import React, { useState, useEffect } from "react";
import { Divider, Menu, MenuItem, IconButton, Tooltip, Box } from "@mui/material";

import NotificationsIcon from "@mui/icons-material/Notifications";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { Typography } from "@mui/material";
import { useQuery } from "@apollo/client/react";
import { getQueryAnnouncement } from "../Announcement/types/getQueryAnnouncement";
import getAnnouncements from "../Announcement/getAnnouncement";
import DialogBoxAnnouncements from "../DialogBox/DialogBoxAnnouncements";
import { GetNotificationCount } from "./types/GetNotificationCount";
import getNotificationsCountQuery from "./GetNotificationCountQuery";
import Loading from "../Loading/Loading";
import theme from "../theme";

const createStyles = () => ({
  notificationsBadge: {
    backgroundColor: theme.palette.error.main,
    color: "#ffffff",
  },
  notificationsIcon: {
    color: "#ffffff",
  },
  notificationsIconActive: {
    color: theme.palette.primary.dark,
  },
  notificationsTitle: {
    fontWeight: "bold",
    fontSize: "0.8125rem",
  },
  notificationsNew: {
    color: "#ffffff !important",
    backgroundColor: theme.palette.primary.main + " !important",
    width: "100%",
    justifyContent: "left !important",
  },
  notificationsUrgent: {
    color: "#ffffff !important",
    backgroundColor: theme.palette.primary.dark + " !important",
    width: "100%",
    justifyContent: "left !important",
  },
  notificationsAbnormal: {
    color: "#ffffff !important",
    backgroundColor: theme.palette.error.main + " !important",
    width: "100%",
    justifyContent: "left !important",
  },
  countNew: {
    color: theme.palette.primary.main + " !important",
    backgroundColor: "#ffffff !important",
    fontWeight: "bold",
    width: "30px !important",
    height: "30px !important",
  },
  countUrgent: {
    color: theme.palette.primary.dark + " !important",
    backgroundColor: "#ffffff !important",
    fontWeight: "bold",
    width: "30px !important",
    height: "30px !important",
  },
  countAbnormal: {
    color: theme.palette.error.main + " !important",
    backgroundColor: "#ffffff !important",
    fontWeight: "bold",
    width: "30px !important",
    height: "30px !important",
  },
  announcement: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: "5px",
    padding: "5px",
    width: "150px",
    whiteSpace: "break-spaces",
    cursor: "default",
  },
  announcementText: {
    fontSize: "0.8rem !important",
    marginTop: "3px !important",
    marginBottom: "3px !important",
  },
  announcementSubject: {
    fontSize: "0.7rem !important",
    marginTop: "3px !important",
    marginBottom: "3px !important",
  },
  withoutHover: {
    backgroundColor: "transparent !important",
    cursor: "default !important",
  },
});

export type NotificationsMenuProps = {
  display?: string;
  ml?: number;
  style?: React.CSSProperties;
  isDesktop?: boolean;
};

const NotificationsMenu: React.FC<NotificationsMenuProps> = (props) => {
  const classes = createStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { data } = useQuery<getQueryAnnouncement>(getAnnouncements, {});
  const [isAnnouncementAvai, setIsAnnouncementAvai] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeResults, setActiveResults] = useState(false);
  const [total, setTotal] = useState(0);
  const [urgent, setUrgent] = useState(0);
  const [abnormal, setAbnormal] = useState(0);
  const [title, setTitle] = useState("Announcement");

  const handleAnnoucementClick = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    if (data) {
      if (data.announcement.announce.length > 0) {
        setIsAnnouncementAvai(true);

        if (data.announcement.announce.length > 1) {
          setTitle("Announcements");
        }
      }
    } else {
      setIsAnnouncementAvai(false);
    }
  }, [data]);

  const { data: notificatiosnCountData, loading: notificatiosnCountLoading } = useQuery<GetNotificationCount>(
    getNotificationsCountQuery,
    {
      pollInterval: 15 * 60 * 1000, // refresh data every 15 min
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (notificatiosnCountData && notificatiosnCountData.getNotificationCount) {
      setActiveResults(notificatiosnCountData.getNotificationCount.total > 0 ? true : false);
      setTotal(notificatiosnCountData.getNotificationCount.total);
      setUrgent(notificatiosnCountData.getNotificationCount.urgent);
      setAbnormal(notificatiosnCountData.getNotificationCount.abnormal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificatiosnCountData]);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          style={{ marginLeft: props.isDesktop ? "15px" : "0px" }}
        >
          <NotificationsIcon sx={activeResults ? classes.notificationsIconActive : classes.notificationsIcon} />
        </IconButton>
      </Tooltip>
      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem style={{ pointerEvents: "none" }} className={`${classes.notificationsTitle} ${classes.withoutHover}`}>
          Daily results summary
        </MenuItem>
        <Divider />
        {notificatiosnCountLoading ? (
          <MenuItem sx={classes.withoutHover}>
            <Loading iconStyle={{ width: "24px", height: "24px" }} mt={0} boxStyle={{ paddingRight: "10px" }} />
          </MenuItem>
        ) : (
          [
            <MenuItem style={{ pointerEvents: "none" }} key={"resulttoday"} sx={classes.withoutHover}>
              <Chip
                avatar={<Avatar sx={classes.countNew}>{total > 999 ? "999+" : total}</Avatar>}
                label="New Results Today"
                sx={classes.notificationsNew}
              />
            </MenuItem>,
            <MenuItem style={{ pointerEvents: "none" }} key={"urgentresult"} sx={classes.withoutHover}>
              <Chip
                avatar={<Avatar sx={classes.countUrgent}>{urgent > 999 ? "999+" : urgent}</Avatar>}
                label="Urgent Results"
                sx={classes.notificationsUrgent}
              />
            </MenuItem>,
            <MenuItem style={{ pointerEvents: "none" }} key={"abnormalresult"} sx={classes.withoutHover}>
              <Chip
                avatar={<Avatar sx={classes.countAbnormal}>{abnormal > 999 ? "999+" : abnormal}</Avatar>}
                label="Abnormal Results"
                sx={classes.notificationsAbnormal}
              />
            </MenuItem>,
          ]
        )}
        {isAnnouncementAvai && [
          <Divider key={"diverbar2"} />,
          <MenuItem key={"announcement"} style={{ pointerEvents: "none", background: "inherit" }}>
            <Box sx={classes.announcement}>
              <Typography sx={classes.announcementText}>
                <strong>{title}</strong>
              </Typography>
              {data?.announcement.announce.map((an, i) => {
                return (
                  <React.Fragment key={i}>
                    <Divider />
                    <Typography sx={classes.announcementSubject}>
                      <strong>{an.subject}</strong> <br />
                      Click{" "}
                      <u style={{ cursor: "pointer", pointerEvents: "auto" }} onClick={handleAnnoucementClick}>
                        here
                      </u>{" "}
                      to learn more
                    </Typography>
                  </React.Fragment>
                );
              })}
            </Box>
          </MenuItem>,
        ]}
      </Menu>
      <DialogBoxAnnouncements
        open={dialogOpen}
        onClose={handleDialogClose}
        data={data as getQueryAnnouncement}
        title={title}
      ></DialogBoxAnnouncements>
    </>
  );
};

export default NotificationsMenu;
